import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createProject } from '../../store/slices/projectSlice';
import { PROJECT_STATUS } from '../../utils/constants';
import api from '../../services/api';

const ProjectModal = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allEmployees, setAllEmployees] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: [],
    status: PROJECT_STATUS.ACTIVE
  });

  useEffect(() => {
    const fetchAllEmployees = async () => {
      try {
        const response = await api.get('/employees', { params: { limit: 1000 } });
        const employees = response.data.data?.employees || [];
        setAllEmployees(Array.isArray(employees) ? employees : []);
      } catch (err) {
        console.error('Failed to load employees:', err);
        setAllEmployees([]);
      }
    };
    fetchAllEmployees();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'members') {
      const options = Array.from(e.target.selectedOptions, (option) => option.value);
      setFormData({ ...formData, members: options });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await dispatch(createProject(formData)).unwrap();
      onSuccess(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create New Project</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label">Project Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Website Redesign"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of the project"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Members (Hold Ctrl/Cmd to select multiple)</label>
                  <select
                    name="members"
                    className="form-select"
                    value={formData.members}
                    onChange={handleChange}
                    multiple
                    style={{ minHeight: '160px' }}
                  >
                    {allEmployees.map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} ({emp.employee_code})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted d-block mt-1">
                    Selected: {formData.members.length > 0
                      ? allEmployees.filter((emp) => formData.members.includes(emp._id)).map((emp) => emp.name).join(', ')
                      : 'None'}
                  </small>
                </div>

                <div className="mb-0">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(PROJECT_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectModal;
